import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const IDLE_MS = 5 * 60 * 1000; // 5 minutes
const WARN_MS = 60 * 1000;      // warn 1 min before logout
const EVENTS  = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export default function IdleTimerProvider({ children }) {
  const { currentUser, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown,   setCountdown]   = useState(60);

  // Stable refs — never cause stale closures
  const logoutFn     = useRef(logout);
  const isLoggedIn   = useRef(!!currentUser);
  const warnActive   = useRef(false);
  const lastActivity = useRef(Date.now());
  const logoutTimer  = useRef(null);
  const warnTimer    = useRef(null);
  const countdownInt = useRef(null);

  useEffect(() => { logoutFn.current    = logout;      }, [logout]);
  useEffect(() => { isLoggedIn.current  = !!currentUser; }, [currentUser]);

  // All timer logic uses only refs — never stale
  const clearTimers = useRef(() => {
    clearTimeout(logoutTimer.current);
    clearTimeout(warnTimer.current);
    clearInterval(countdownInt.current);
  });

  const doLogout = useRef(() => {
    clearTimers.current();
    warnActive.current = false;
    setShowWarning(false);
    logoutFn.current();
    try {
      const role = JSON.parse(localStorage.getItem('userData') || '{}').role;
      window.location.href = role === 'ADMIN' ? '/secure-admin-portal/login' : '/login';
    } catch {
      window.location.href = '/login';
    }
  });

  const schedule = useRef(() => {
    if (!isLoggedIn.current) return;
    clearTimers.current();
    warnActive.current = false;
    setShowWarning(false);

    warnTimer.current = setTimeout(() => {
      warnActive.current = true;
      setShowWarning(true);
      setCountdown(Math.floor(WARN_MS / 1000));
      countdownInt.current = setInterval(() => {
        setCountdown(p => { if (p <= 1) { clearInterval(countdownInt.current); return 0; } return p - 1; });
      }, 1000);
    }, IDLE_MS - WARN_MS);

    logoutTimer.current = setTimeout(() => doLogout.current(), IDLE_MS);
  });

  const onActivity = useRef(() => {
    if (!isLoggedIn.current || warnActive.current) return;
    lastActivity.current = Date.now();
    schedule.current();
  });

  // Register listeners once on mount, never re-register
  useEffect(() => {
    const act = () => onActivity.current();
    EVENTS.forEach(e => window.addEventListener(e, act, { passive: true }));

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const away = Date.now() - lastActivity.current;
      if (away >= IDLE_MS && isLoggedIn.current) doLogout.current();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimers.current();
      EVENTS.forEach(e => window.removeEventListener(e, act));
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Start/stop timer when login state changes
  useEffect(() => {
    if (currentUser) {
      lastActivity.current = Date.now();
      schedule.current();
    } else {
      clearTimers.current();
      warnActive.current = false;
      setShowWarning(false);
    }
  }, [currentUser]);

  const stayLoggedIn = () => {
    warnActive.current = false;
    setShowWarning(false);
    lastActivity.current = Date.now();
    schedule.current();
  };

  if (!currentUser) return <>{children}</>;

  return (
    <>
      {children}
      {showWarning && (
        <div style={overlay}>
          <div style={modal}>
            <h3 style={{ margin: '0 0 10px', color: '#d32f2f' }}>Session Expiring</h3>
            <p style={{ margin: '0 0 16px', color: '#555' }}>
              No activity detected. Logging out in <strong>{countdown}</strong> second{countdown !== 1 ? 's' : ''}.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => doLogout.current()} style={btnSecondary}>Logout Now</button>
              <button onClick={stayLoggedIn} style={btnPrimary} autoFocus>Stay Logged In</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const overlay      = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 };
const modal        = { background: '#fff', borderRadius: '8px', padding: '24px', width: '360px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' };
const btnPrimary   = { padding: '8px 16px', border: 'none',           borderRadius: '4px', background: '#1976d2', color: '#fff', cursor: 'pointer', fontSize: '14px' };
const btnSecondary = { padding: '8px 16px', border: '1px solid #ccc', borderRadius: '4px', background: '#fff',    color: '#333', cursor: 'pointer', fontSize: '14px' };
